import { useInfiniteQuery, useQueryClient } from '@northladder/services';

import { CREDIT_LIMIT_QUERY_KEY } from '../../customer-service';

import { ACCOUNT_SERVICE_API } from '../accountServiceApiClient';
import { IAccountStatementUrlOptions } from '../types';

import { FETCH_PAYMENT_STATUS_KEY } from './usePaymentStatsQuery';

/**
 * This is to track the account transactions query in react query cache.
 */
const ACCOUNT_TRANSACTIONS_QUERY_KEY = 'account-transactions-query-key';

/**
 * This hook calls the account statements api to get all the bulk purchases and
 * transaction details.
 */
export const useAccountStatementQuery = (
  queryParams: IAccountStatementUrlOptions
) => {
  const { fromDate, toDate, search, viewType, filterQuery } = queryParams;

  const {
    data,
    isError,
    error,
    isFetching,
    isPending,
    refetch,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      ACCOUNT_TRANSACTIONS_QUERY_KEY,
      fromDate,
      toDate,
      search,
      viewType,
      filterQuery,
    ],
    networkMode: 'always',
    initialPageParam: 1,
    retry: 3,
    queryFn: () => ACCOUNT_SERVICE_API.fetchAccountStatement(queryParams),
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = allPages.length + 1;
      const currentCount = allPages.length * queryParams.count;

      return currentCount < lastPage.count ? nextPage : undefined;
    },
  });
  return {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isPending,
    refetch,
  };
};

/**
 * Transaction summary query invalidation util. It will invalidate Credit limit
 * query and Payment status query
 */
export function useInvalidateTransactionsSummaryQueries() {
  const queryClient = useQueryClient();

  const handleInvalidateQueries = () => {
    queryClient.invalidateQueries({
      queryKey: [CREDIT_LIMIT_QUERY_KEY],
    });
    queryClient.invalidateQueries({
      queryKey: [FETCH_PAYMENT_STATUS_KEY],
    });
  };

  return { onInvalidateTransactionSummaryQueries: handleInvalidateQueries };
}
